import { useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import type { DeliveryScopeView, WorkItemView } from '@vit/contracts';
import { Button, Checkbox, Dialog, DialogContent, DialogFooter, Field, Input, NativeSelect, Textarea, toast } from '@vit/ui';
import { newIdempotencyKey, useApiMutation, useApiQuery } from '../../lib/api.js';
import { toastError } from '../../lib/errors.js';
import { useTranslation } from '../../lib/i18n.js';

interface DraftRequirement {
  serviceId: string;
  environmentId: string;
  targetSlot: string;
  changes: Array<{ kind: 'PULL_REQUEST' | 'COMMIT' | 'ARTIFACT'; ref: string; repositoryId?: string }>;
}

/**
 * Declares which services/production targets and immutable changes this work needs.
 * Completion requires a confirmed, nonempty scope; agents and heuristics may only prefill it.
 */
export function DeliveryScopeEditor({ item, scope }: { item: WorkItemView; scope: DeliveryScopeView }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const services = useApiQuery('listServices', { query: {} }, { enabled: open });
  const prs = useApiQuery('listPullRequests', { query: { workItemId: item.id } }, { enabled: open });
  const save = useApiMutation('setDeliveryScope', { invalidate: ['getDeliveryScope', 'getWorkItem'] });
  const [requirements, setRequirements] = useState<DraftRequirement[]>([]);
  const [rationale, setRationale] = useState('');
  const [confirm, setConfirm] = useState(true);

  const start = () => {
    setRequirements(
      scope.requirements.length
        ? scope.requirements.map((r) => ({ serviceId: r.service.id, environmentId: r.environment.id, targetSlot: r.targetSlot ?? '', changes: r.changes.map((c) => ({ kind: c.kind, ref: c.ref, ...(c.repositoryId ? { repositoryId: c.repositoryId } : {}) })) }))
        : scope.suggestions.map((s) => ({ serviceId: s.serviceId, environmentId: '', targetSlot: '', changes: [] })),
    );
    setRationale(scope.rationale ?? '');
    setOpen(true);
  };

  const serviceById = useMemo(() => new Map((services.data?.data ?? []).map((s) => [s.id, s])), [services.data]);

  const submit = async () => {
    try {
      await save.mutateAsync({
        params: { id: item.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          expectedVersion: item.version,
          rationale,
          confirm,
          requirements: requirements.filter((r) => r.serviceId && r.environmentId).map((r) => ({ serviceId: r.serviceId, environmentId: r.environmentId, ...(r.targetSlot ? { targetSlot: r.targetSlot } : {}), changes: r.changes.filter((c) => c.ref.trim()) })),
        },
      });
      toast.success(t('common.changesSaved'));
      setOpen(false);
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <>
      <Button size="sm" variant="secondary" onClick={start}>
        <Pencil /> {t('common.edit')}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent title={t('work.detail.delivery')} description={t('status.policyHelp.PRODUCTION')} className="w-[min(720px,calc(100vw-2rem))]">
          <div className="flex flex-col gap-4">
            {requirements.map((r, index) => {
              const service = serviceById.get(r.serviceId);
              const update = (patch: Partial<DraftRequirement>) => setRequirements((list) => list.map((x, i) => (i === index ? { ...x, ...patch } : x)));
              return (
                <div key={index} className="flex flex-col gap-2 rounded-[var(--radius)] border border-border p-3">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_120px_auto]">
                    <NativeSelect value={r.serviceId} onChange={(e) => update({ serviceId: e.target.value, environmentId: '' })} aria-label="Service">
                      <option value="">Service…</option>
                      {(services.data?.data ?? []).map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </NativeSelect>
                    <NativeSelect value={r.environmentId} onChange={(e) => update({ environmentId: e.target.value })} aria-label="Environment">
                      <option value="">Environment…</option>
                      {(service?.environments ?? []).map((env) => (
                        <option key={env.id} value={env.id}>
                          {env.name} ({env.classification})
                        </option>
                      ))}
                    </NativeSelect>
                    <Input value={r.targetSlot} onChange={(e) => update({ targetSlot: e.target.value })} placeholder="slot" aria-label="Target slot" />
                    <Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setRequirements((list) => list.filter((_, i) => i !== index))}>
                      <Trash2 />
                    </Button>
                  </div>
                  <div className="flex flex-col gap-1">
                    {r.changes.map((c, ci) => (
                      <div key={ci} className="flex gap-2">
                        <NativeSelect value={c.kind} className="w-36" onChange={(e) => update({ changes: r.changes.map((x, j) => (j === ci ? { ...x, kind: e.target.value as 'COMMIT' } : x)) })} aria-label="Change kind">
                          <option value="PULL_REQUEST">Pull request</option>
                          <option value="COMMIT">Commit</option>
                          <option value="ARTIFACT">Artifact digest</option>
                        </NativeSelect>
                        {c.kind === 'PULL_REQUEST' ? (
                          <NativeSelect value={c.ref} onChange={(e) => update({ changes: r.changes.map((x, j) => (j === ci ? { ...x, ref: e.target.value } : x)) })} aria-label="Pull request">
                            <option value="">PR…</option>
                            {(prs.data?.data ?? []).map((pr) => (
                              <option key={pr.id} value={pr.id}>
                                {pr.repository.fullName}#{pr.number} {pr.title}
                              </option>
                            ))}
                          </NativeSelect>
                        ) : (
                          <Input value={c.ref} onChange={(e) => update({ changes: r.changes.map((x, j) => (j === ci ? { ...x, ref: e.target.value } : x)) })} placeholder={c.kind === 'COMMIT' ? 'full commit SHA' : 'sha256:…'} aria-label="Reference" />
                        )}
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" className="self-start" onClick={() => update({ changes: [...r.changes, { kind: 'PULL_REQUEST', ref: '' }] })}>
                      <Plus /> Change
                    </Button>
                  </div>
                </div>
              );
            })}
            <Button variant="secondary" size="sm" className="self-start" onClick={() => setRequirements((list) => [...list, { serviceId: '', environmentId: '', targetSlot: '', changes: [] }])}>
              <Plus /> Service
            </Button>
            <Field label={t('common.reason')} required>
              <Textarea value={rationale} onChange={(e) => setRationale(e.target.value)} rows={2} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={confirm} onCheckedChange={(v) => setConfirm(v === true)} /> {t('status.scope.CONFIRMED')}
            </label>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={() => void submit()} loading={save.isPending} disabled={!rationale.trim()}>
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
